import { useEffect, useMemo, useState } from 'react';
import {
  Badge,
  Button,
  Field,
  Input,
  MessageBar,
  MessageBarBody,
  MessageBarTitle,
  makeStyles,
} from '@fluentui/react-components';
import { useInfiniteQuery } from '@tanstack/react-query';
import { RefreshCw, Search } from 'lucide-react';

import {
  MIN_SEARCH_LENGTH,
  extractConsentError,
  isGraphError,
  searchSourceDevices,
  sourceDeviceSearchErrorCode,
} from '../api/sourceDevices';
import { ApiError } from '../api/client';
import { SourceDevice, SourceDevicesResponse } from '../types/sourceDevices';
import { DataTable, DataTableColumn } from './DataTable';
import RelativeTime from './RelativeTime';
import ConsentMessageBar from './identity/ConsentMessageBar';
import { IDENTIFIER } from '../theme/tokens';

const DEBOUNCE_MS = 300;

export interface SourceDeviceLookupProps {
  migrationId: string;
  // The serials the admin has staged for assignment (shared with the paste
  // tab). Selecting a device row toggles its serial in this set; a single
  // footer submit assigns the union.
  selectedSerials: Set<string>;
  onToggleSerial: (serial: string) => void;
  disabled?: boolean;
}

const useStyles = makeStyles({
  body: {
    display: 'flex',
    flexDirection: 'column',
    rowGap: '12px',
  },
  hint: {
    fontSize: '12px',
    color: '#52525b',
  },
  resultsSurface: {
    borderTopWidth: '1px',
    borderRightWidth: '1px',
    borderBottomWidth: '1px',
    borderLeftWidth: '1px',
    borderTopStyle: 'solid',
    borderRightStyle: 'solid',
    borderBottomStyle: 'solid',
    borderLeftStyle: 'solid',
    borderTopColor: '#e4e4e7',
    borderRightColor: '#e4e4e7',
    borderBottomColor: '#e4e4e7',
    borderLeftColor: '#e4e4e7',
    borderTopLeftRadius: '6px',
    borderTopRightRadius: '6px',
    borderBottomLeftRadius: '6px',
    borderBottomRightRadius: '6px',
    maxHeight: '320px',
    overflowY: 'auto',
  },
  mono: {
    ...IDENTIFIER,
  },
  loadMoreRow: {
    display: 'flex',
    justifyContent: 'center',
    paddingTop: '8px',
  },
});

// Lookup-mode body of the T2T assign flow: enter a full serial → exact-match
// search against the migration's SOURCE tenant Intune managed devices
// (FD-112) → confirm the real device(s) and stage their serial(s) for
// assignment. This is a validate/lookup, not a fuzzy browse — Graph only
// supports `eq` on managedDevice.serialNumber, so a partial serial returns no
// rows. Selected serials feed the unchanged serial-keyed add-devices submit.
export default function SourceDeviceLookup({
  migrationId,
  selectedSerials,
  onToggleSerial,
  disabled,
}: SourceDeviceLookupProps) {
  const styles = useStyles();
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');

  // 300ms debounce so typing does not hammer Graph (UserPickerDialog precedent).
  useEffect(() => {
    const handle = window.setTimeout(() => setSearch(searchInput.trim()), DEBOUNCE_MS);
    return () => window.clearTimeout(handle);
  }, [searchInput]);

  const searchReady = search.length >= MIN_SEARCH_LENGTH;

  const query = useInfiniteQuery<SourceDevicesResponse, Error>({
    queryKey: ['source-devices', migrationId, search],
    queryFn: ({ pageParam }) =>
      searchSourceDevices(migrationId, {
        search,
        skipToken: (pageParam as string | null | undefined) ?? undefined,
      }),
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage) => lastPage.skipToken ?? undefined,
    enabled: searchReady,
    retry: (failureCount, error) => {
      // Consent-class 409 and D2C 400 are definitive — do not retry. Transient
      // 502 GRAPH_ERROR retries once (handled by the default predicate below).
      if (error instanceof ApiError && (error.status === 409 || error.status === 400)) {
        return false;
      }
      return failureCount < 1;
    },
  });

  const devices = useMemo<SourceDevice[]>(
    () => (query.data ? query.data.pages.flatMap((p) => p.devices) : []),
    [query.data],
  );

  const columns = useMemo<DataTableColumn<SourceDevice>[]>(
    () => [
      {
        key: 'deviceName',
        header: 'Device',
        render: (d) => (
          <span className="font-medium text-zinc-900">{d.deviceName ?? '—'}</span>
        ),
      },
      {
        key: 'serialNumber',
        header: 'Serial',
        numeric: true,
        render: (d) => <span>{d.serialNumber ?? '—'}</span>,
      },
      {
        key: 'os',
        header: 'OS',
        render: (d) => (
          <span>
            {d.operatingSystem ?? '—'}
            {d.osVersion ? ` ${d.osVersion}` : ''}
          </span>
        ),
      },
      {
        key: 'model',
        header: 'Model',
        render: (d) => (
          <span>
            {[d.manufacturer, d.model].filter(Boolean).join(' ') || '—'}
          </span>
        ),
      },
      {
        key: 'complianceState',
        header: 'Compliance',
        render: (d) =>
          d.complianceState ? (
            <Badge
              appearance="tint"
              color={d.complianceState.toLowerCase() === 'compliant' ? 'success' : 'warning'}
            >
              {d.complianceState}
            </Badge>
          ) : (
            <span className="text-zinc-400">—</span>
          ),
      },
      {
        key: 'userPrincipalName',
        header: 'Enrolled user',
        render: (d) => <span>{d.userPrincipalName ?? '—'}</span>,
      },
      {
        key: 'lastSyncDateTime',
        header: 'Last sync',
        numeric: true,
        render: (d) => <RelativeTime iso={d.lastSyncDateTime} />,
      },
    ],
    [],
  );

  const consent = query.isError ? extractConsentError(query.error) : null;

  const errorNode = query.isError
    ? consent
      ? <ConsentMessageBar consent={consent} />
      : <LookupErrorBar error={query.error} onRetry={() => query.refetch()} />
    : undefined;

  const selection = useMemo(
    () => ({
      selectedKeys: selectedSerials,
      onSelectionChange: (next: Set<string>) => {
        // DataTable hands back the whole next set; translate the diff into
        // per-serial toggles so the parent's staged set stays the source of
        // truth (also drives the paste-tab union + footer count).
        const added = [...next].filter((s) => !selectedSerials.has(s));
        const removed = [...selectedSerials].filter((s) => !next.has(s));
        for (const s of [...added, ...removed]) onToggleSerial(s);
      },
      keyForSelection: (d: SourceDevice) => d.serialNumber ?? d.id,
    }),
    [selectedSerials, onToggleSerial],
  );

  return (
    <div className={styles.body}>
      <div className={styles.hint}>
        Enter a full device serial number to look it up in the source tenant, confirm the
        real device, and stage it for assignment. Matching is exact.
      </div>
      <Field>
        <Input
          value={searchInput}
          onChange={(_, data) => setSearchInput(data.value)}
          placeholder="Enter a full serial number"
          contentBefore={<Search size={14} strokeWidth={1.5} />}
          aria-label="Search source-tenant devices by serial number"
          disabled={disabled}
        />
      </Field>
      {!searchReady ? (
        <div className={styles.hint}>
          Type at least {MIN_SEARCH_LENGTH} characters of a serial number to search the
          source tenant.
        </div>
      ) : (
        <>
          <div className={styles.resultsSurface}>
            <DataTable
              columns={columns}
              data={devices}
              keyForRow={(d) => d.id}
              isLoading={query.isLoading}
              loadingRowCount={3}
              error={errorNode}
              selection={selection}
              emptyState={
                <span className={styles.hint}>
                  No device with serial “{search}” in the source tenant.
                </span>
              }
            />
          </div>
          {query.hasNextPage && !query.isError && (
            <div className={styles.loadMoreRow}>
              <Button
                appearance="secondary"
                size="small"
                onClick={() => query.fetchNextPage()}
                disabled={query.isFetchingNextPage}
              >
                {query.isFetchingNextPage ? 'Loading…' : 'Load more'}
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// Non-consent error rendering for the lookup. 502 GRAPH_ERROR is transient (the
// admin can retry, or fall back to the paste-serials tab); D2C_HAS_NO_SOURCE_TENANT
// should not occur (lookup is T2T-gated) but is handled defensively.
function LookupErrorBar({ error, onRetry }: { error: unknown; onRetry: () => void }) {
  const code = sourceDeviceSearchErrorCode(error);
  const transient = code === 'GRAPH_ERROR' || isGraphError(error);

  const title =
    code === 'D2C_HAS_NO_SOURCE_TENANT'
      ? 'No source tenant'
      : transient
        ? 'Source tenant temporarily unavailable'
        : 'Could not search the source tenant';
  const body =
    code === 'D2C_HAS_NO_SOURCE_TENANT'
      ? 'This migration has no source tenant to search. Use the Paste serials tab instead.'
      : transient
        ? 'Microsoft Graph did not respond. This is usually transient — retry, or paste serials directly.'
        : errorMessage(error);

  return (
    <MessageBar intent={transient ? 'warning' : 'error'}>
      <MessageBarBody>
        <MessageBarTitle>{title}</MessageBarTitle>
        {body}
      </MessageBarBody>
      {transient && (
        <Button
          size="small"
          appearance="subtle"
          icon={<RefreshCw size={14} strokeWidth={1.5} />}
          onClick={onRetry}
        >
          Retry
        </Button>
      )}
    </MessageBar>
  );
}

function errorMessage(err: unknown): string {
  if (err instanceof Error) return err.message;
  return 'Unexpected error';
}
